import { Menu } from "@base-ui/react/menu";
import { ArrowsClockwiseIcon, BookOpenTextIcon, CaretDownIcon, CaretLeftIcon, CaretRightIcon, ChartBarIcon, CheckCircleIcon, CheckIcon, CubeIcon, DotsThreeIcon, GearSixIcon, IdentificationCardIcon, InfoIcon, KeyIcon, MagnifyingGlassIcon, PlusIcon, ShieldCheckIcon, SidebarSimpleIcon, SignOutIcon, SlidersHorizontalIcon, SquaresFourIcon, TerminalWindowIcon, TrayIcon, UserPlusIcon, UsersIcon } from "@phosphor-icons/react";
import { useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useRouterState } from "@tanstack/react-router";
import type * as C from "@zooi/contracts";
import { type ComponentType, Fragment, type ReactNode, useState } from "react";
import { authApi } from "./api.ts";
import { resetAnalytics } from "./analytics.ts";
import { lastProduct } from "./current-product.ts";
import { number } from "./format.ts";
import { AREA_LABELS as A, DASHBOARD_LABELS, GATE_LABELS, SETTINGS_LABELS, SETTINGS_NAV_LABELS as N, SHELL_LABELS as L, WORKSPACE_LABELS as W, SIDEBAR_LABELS as S, WEEK_LABELS } from "./labels.ts";
import { readPref, writePref } from "./prefs.ts";
import { isProductOpen, PRODUCT_PAGES, type ProductPageKey, productBadges, productOfPath, productPageOfPath, readOpenProducts } from "./sidebar-items.ts";
import { ErrorText, Kbd } from "./ui.tsx";
import { activateWorkspace } from "./workspace-settings.tsx";
import { ProductMark } from "./ui/props.tsx";

// The sidebar after Linear's (docs/design-system.md §0.2, plan M7 step 7c):
// the workspace menu with Search on top, the overview, Inbox, the workspace's
// Playbooks, then one collapsible group per product with its pages. A new
// view is a tab or a filter inside a page, never a new entry here.

type Icon = ComponentType<{ size?: number; weight?: "fill" | "regular" }>;

/** A product's pages with their icons and labels, in sidebar order. */
export const PRODUCT_PAGE_META: Record<ProductPageKey, { label: string; icon: Icon }> = {
  week: { label: WEEK_LABELS.title, icon: ArrowsClockwiseIcon },
  campaigns: { label: A.campaigns, icon: CubeIcon },
  actions: { label: A.actions, icon: CheckCircleIcon },
  data: { label: A.data, icon: ChartBarIcon },
  profile: { label: A.profile, icon: IdentificationCardIcon },
};

const OPEN_KEY = "sidebar-products";

export function Sidebar({ me, products, items, onSearch, onHide }: {
  me: C.Me; products: C.Product[]; items: C.InboxItem[] | undefined; onSearch: () => void; onHide: () => void;
}) {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const count = items?.length ?? 0;
  const badges = productBadges(items ?? []);
  const current = productOfPath(pathname);
  // Off a product's pages, the product last opened stays open.
  const focus = current ?? lastProduct();
  const [open, setOpen] = useState(() => readOpenProducts(readPref(OPEN_KEY)));
  const toggle = (id: string) => setOpen((value) => {
    const next = { ...value, [id]: !isProductOpen(value, id, focus) };
    writePref(OPEN_KEY, JSON.stringify(next));
    return next;
  });
  return <>
    <div className="z-side-top">
      <WorkspaceMenu me={me} />
      <button type="button" className="z-iconbtn" aria-label={S.search} title={S.search} onClick={onSearch}><MagnifyingGlassIcon size={16} /></button>
      <button type="button" className="z-iconbtn z-side-toggle" aria-label={L.hideSidebar} title={L.hideSidebar} onClick={onHide}><SidebarSimpleIcon size={16} /></button>
    </div>
    <nav className="z-side-group" aria-label={L.workspace}>
      <Link to="/" className="z-nav" activeOptions={{ exact: true }} activeProps={{ className: "is-active", "aria-current": "page" }} title={`${DASHBOARD_LABELS.title} (G O)`}>
        <SquaresFourIcon size={16} /><span className="z-nav-label">{DASHBOARD_LABELS.title}</span>
      </Link>
      <Link to="/inbox" className="z-nav" activeProps={{ className: "is-active", "aria-current": "page" }} aria-label={count ? S.inboxWaiting(count) : A.inbox} title={`${A.inbox} (G I)`}>
        <TrayIcon size={16} /><span className="z-nav-label">{A.inbox}</span>{count > 0 && <span className="z-nav-meta">{number(count)}</span>}
      </Link>
      <Link to="/playbooks" className="z-nav" activeProps={{ className: "is-active", "aria-current": "page" }} title={`${A.playbooks} (G L)`}>
        <BookOpenTextIcon size={16} /><span className="z-nav-label">{A.playbooks}</span>
      </Link>
    </nav>
    <nav className="z-side-group" aria-label={S.yourProducts}>
      <div className="z-side-label">{S.yourProducts}</div>
      {products.map((product, index) => {
        const expanded = isProductOpen(open, product.id, focus);
        const badge = badges.get(product.id);
        return <div key={product.id} className="z-side-product">
          <div className="z-side-product-head">
            <button type="button" className="z-nav is-product" aria-expanded={expanded} onClick={() => toggle(product.id)}>
              <ProductMark productId={product.id} url={product.url} name={product.name} tone={index % 2 === 0 ? 1 : 2} size={16} />
              <span className="z-nav-label">{product.name}</span>
              {badge && badge.count > 0 && <span className="z-nav-meta">{number(badge.count)}</span>}
              <span className="z-nav-caret" aria-hidden>{expanded ? <CaretDownIcon size={16} weight="bold" /> : <CaretRightIcon size={16} weight="bold" />}</span>
            </button>
            <ProductMenu product={product} />
          </div>
          {expanded && PRODUCT_PAGES.map((page) => {
            const meta = PRODUCT_PAGE_META[page.key];
            const here = current === product.id && productPageOfPath(pathname) === page.key;
            return <Link key={page.key} to={`/p/$productId/${page.key}`} params={{ productId: product.id }} className={`z-nav is-sub${here ? " is-active" : ""}`} aria-current={here ? "page" : undefined} title={`${meta.label} (G ${page.jump.toUpperCase()})`}>
              <meta.icon size={16} /><span className="z-nav-label">{meta.label}</span>
              {page.key === "campaigns" && badge?.readingDue && <span className="z-dot" data-tone="orange" role="img" aria-label={S.readingDue} title={S.readingDue} />}
            </Link>;
          })}
        </div>;
      })}
      {me.product_ids === null && <Link to="/new" className="z-nav z-side-add" activeProps={{ className: "is-active", "aria-current": "page" }}><PlusIcon size={16} /><span className="z-nav-label">{S.addProduct}</span></Link>}
    </nav>
  </>;
}

/** A product group's "…": its settings and its agent. */
function ProductMenu({ product }: { product: C.Product }) {
  const navigate = useNavigate();
  return <Menu.Root>
    <Menu.Trigger className="z-iconbtn z-side-product-menu" aria-label={S.productMenu(product.name)} title={S.productMenu(product.name)}><DotsThreeIcon size={16} weight="bold" /></Menu.Trigger>
    <Menu.Portal><Menu.Positioner sideOffset={4} align="start"><Menu.Popup className="z-layer">
      <Menu.Item className="z-menu-item" onClick={() => { void navigate({ to: "/settings/products/$productId", params: { productId: product.id } }); }}><GearSixIcon size={16} />{S.productSettings}</Menu.Item>
      <Menu.Item className="z-menu-item" onClick={() => { void navigate({ to: "/settings/products/$productId/agent", params: { productId: product.id } }); }}><TerminalWindowIcon size={16} />{S.agentAndRuns}</Menu.Item>
    </Menu.Popup></Menu.Positioner></Menu.Portal>
  </Menu.Root>;
}

/**
 * Settings' own navigation in place of the sidebar, as in Linear (plan M7
 * step 7d): back to where the person was, the workspace's sections, then each
 * product's.
 */
export function SettingsSidebar({ me, products, backTo }: { me: C.Me; products: C.Product[]; backTo: string }) {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const item = (to: string, label: string, icon: ReactNode, key: string) => <Link key={key} to={to} activeOptions={{ exact: true }} className={`z-nav${pathname === to ? " is-active" : ""}`} aria-current={pathname === to ? "page" : undefined}>{icon}<span className="z-nav-label">{label}</span></Link>;
  return <>
    <div className="z-side-top">
      <Link to={backTo} className="z-nav z-side-back"><CaretLeftIcon size={14} /><span className="z-nav-label">{N.back}</span><Kbd>Esc</Kbd></Link>
    </div>
    <nav className="z-side-group" aria-label={N.nav}>
      <div className="z-side-label">{N.workspace(me.workspace.name)}</div>
      {item("/settings/workspace", SETTINGS_LABELS.workspace, <SquaresFourIcon size={16} />, "workspace")}
      {item("/settings/members", N.members, <UsersIcon size={16} />, "members")}
      {item("/settings/tokens", N.tokens, <KeyIcon size={16} />, "tokens")}
      {item("/settings/preferences", N.preferences, <SlidersHorizontalIcon size={16} />, "preferences")}
      {me.operator && item("/settings/gate", GATE_LABELS.tab, <ChartBarIcon size={16} />, "gate")}
      {products.map((product, index) => <Fragment key={product.id}>
        <div className="z-side-label"><ProductMark productId={product.id} url={product.url} name={product.name} tone={index % 2 === 0 ? 1 : 2} size={14} />{product.name}</div>
        {item(`/settings/products/${product.id}`, N.basics, <InfoIcon size={16} />, `${product.id}-basics`)}
        {item(`/settings/products/${product.id}/agent`, N.agent, <TerminalWindowIcon size={16} />, `${product.id}-agent`)}
        {item(`/settings/products/${product.id}/grants`, N.grants, <ShieldCheckIcon size={16} />, `${product.id}-grants`)}
      </Fragment>)}
    </nav>
  </>;
}

/** The workspace on top: settings, inviting, preferences, other workspaces and signing out. */
function WorkspaceMenu({ me }: { me: C.Me }) {
  const client = useQueryClient();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>();
  const switchWorkspace = async (id: string) => {
    if (id === me.workspace.id) return;
    setBusy(true); setError(undefined);
    try {
      await activateWorkspace(id);
    }
    catch (cause) { setError(cause); }
    finally { setBusy(false); }
  };
  return <>
    <Menu.Root>
      <Menu.Trigger className="z-switcher" aria-label={S.workspaceMenu} title={me.user.email} disabled={busy}>
        <span className="z-ws-mark" aria-hidden>{me.workspace.name.slice(0, 1).toUpperCase()}</span>
        <span>{busy ? S.switching : me.workspace.name}</span><CaretDownIcon size={16} weight="bold" />
      </Menu.Trigger>
      <Menu.Portal><Menu.Positioner sideOffset={4} align="start"><Menu.Popup className="z-layer">
        <div className="z-menu-label">{me.workspace.name} · {SETTINGS_LABELS.role[me.workspace.role as keyof typeof SETTINGS_LABELS.role]}</div>
        <div className="z-menu-label">{me.user.name ? `${me.user.name} · ${me.user.email}` : me.user.email}</div>
        <Menu.Item className="z-menu-item" onClick={() => { void navigate({ to: "/settings/workspace" }); }}><GearSixIcon size={16} />{A.settings}</Menu.Item>
        {["owner", "admin"].includes(me.workspace.role) && <Menu.Item className="z-menu-item" onClick={() => { void navigate({ to: "/settings/members" }); }}><UserPlusIcon size={16} />{S.inviteMembers}</Menu.Item>}
        <Menu.Item className="z-menu-item" onClick={() => { void navigate({ to: "/settings/preferences" }); }}><SlidersHorizontalIcon size={16} />{SETTINGS_LABELS.preferences}</Menu.Item>
        {me.workspaces.length > 1 && <>
          <div className="z-menu-sep" />
          <div className="z-menu-label">{S.workspaces}</div>
          {me.workspaces.map((workspace) => <Menu.Item key={workspace.id} className="z-menu-item" onClick={() => { void switchWorkspace(workspace.id); }}>
            <SquaresFourIcon size={16} /><span className="z-menu-text">{workspace.name}</span>{workspace.id === me.workspace.id && <CheckIcon size={14} />}
          </Menu.Item>)}
        </>}
        <Menu.Item className="z-menu-item" onClick={() => { void navigate({ to: "/settings/workspace", search: { create: true } }); }}><PlusIcon size={16} />{W.create}</Menu.Item>
        <div className="z-menu-sep" />
        <Menu.Item className="z-menu-item" onClick={async () => { await authApi.signOut().catch(() => {}); resetAnalytics(); client.clear(); void navigate({ to: "/sign-in" }); }}><SignOutIcon size={16} />{L.signOut}</Menu.Item>
      </Menu.Popup></Menu.Positioner></Menu.Portal>
    </Menu.Root>
    {error != null && <ErrorText error={error} />}
  </>;
}
